import { Button, ESectionColor, Section, SectionSubtitle, SectionTitle } from '@web_opxp/components';
import { AuthSelectors, ETagType, LanguageSelectors, renderStringToReact, useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import * as HistoryService from '../base/historyService';
import { getTrainingUrl } from '../base/historyService';
import MoneybackGuarantee from '../components/moneyback/MoneybackGuarantee';
import TrustPilotBadge from '../components/trustpilotBadge/TrustPilotBadge';
import Day0Banner from '../coupon/Day0Banner';
import { isChargebee, ISubscription, ManageActions, ManageSelectors } from '../manage';
import { IState } from '../store/state';
import ProductItem from './ProductItem';
import SplitTestWrapper from './SplitWrapper';
import './Subscribe.scss';
import * as Selectors from './selectors';
import { SubscribeActions } from './slice';
import { IProductData } from './types';
import { getCountryFromProductList, getProductId } from './utils';

export interface ISubscribeAppProps {
  products: IProductData[];
  language?: string;
  isDiscounted: boolean;
  isPremium: boolean;
  subscriptions?: ISubscription[];
  onProductSelect: (productId: number) => void;
  onStartManageApp: () => void;
  session?: string;
  isLoggedIn?: boolean;
}

export const SubscribeApp: React.FC<ISubscribeAppProps> = ({
  products,
  language,
  isDiscounted,
  isPremium,
  subscriptions,
  onProductSelect,
  onStartManageApp,
  session,
  isLoggedIn,
}) => {
  React.useEffect(() => {
    if (isLoggedIn) onStartManageApp();
  }, [onStartManageApp, isLoggedIn]);
  const cancelledSubscription =
    subscriptions === undefined ||
    subscriptions.length === 0 ||
    subscriptions.every((subscription) => {
      if (isChargebee(subscription)) {
        return subscription.status === 'cancelled';
      }
      return false;
    });
  const country = getCountryFromProductList(products);
  const renderProduct = (productData: IProductData) => {
    const productId = getProductId(productData);
    return (
      <div key={productId} className={'col-4 col-m-4' + (isDiscounted ? ' discounted-product' : '')}>
        <ProductItem language={language} productData={productData} onClick={onProductSelect} />
      </div>
    );
  };

  const redirectToManage = () => {
    HistoryService.redirectToManage();
  };

  const { t } = useTranslation();
  const trainingUrl = getTrainingUrl('', language, session);

  if (isPremium) {
    // User is already premium
    return (
      <Section color={ESectionColor.blue} className={'subscribe-product-section' + (isDiscounted ? ' discount' : '')}>
        <TrustPilotBadge />
        <SectionTitle>{t('Subscribe.Already.Premium.Title')}</SectionTitle>
        <SectionSubtitle wrapperTag="div">
          {renderStringToReact(t('Subscribe.Already.Premium.Description'), ETagType.div)}
        </SectionSubtitle>
        <div className="buttons">
          <Button label={t('Subscribe.Goto.Manage')} onClick={redirectToManage} />
        </div>
        <MoneybackGuarantee country={country} />
        <TrustPilotBadge />
      </Section>
    );
  }

  if (!cancelledSubscription) {
    // User is not premium but has subscription status other than cancelled, this means an error has occurred
    return (
      <Section color={ESectionColor.blue} className={'subscribe-product-section' + (isDiscounted ? ' discount' : '')}>
        <TrustPilotBadge />
        <SectionTitle>{t('Subscribe.Premium.Error.Title')}</SectionTitle>
        <SectionSubtitle wrapperTag="div">
          {renderStringToReact(t('Subscribe.Premium.Error.Description'), ETagType.div)}
        </SectionSubtitle>
        <MoneybackGuarantee country={country} />
        <TrustPilotBadge />
      </Section>
    );
  }

  return (
    <>
      <Day0Banner />
      <Section color={ESectionColor.blue} className={'subscribe-product-section' + (isDiscounted ? ' discount' : '')}>
        <TrustPilotBadge />
        <SectionTitle>{t('Subscribe.Unlock.Premium')}</SectionTitle>
        <div className="row subscribe-products-container">{products.map(renderProduct)}</div>
        <MoneybackGuarantee country={country} />
        <SplitTestWrapper>
          <div className="skip-premium">
            <a href={trainingUrl}>{`> ${t('Subscribe.Skip.Premium')}`}</a>
          </div>
        </SplitTestWrapper>
        <TrustPilotBadge />
      </Section>
    </>
  );
};

// maps external global state to application properties
const mapStateToProps = (state: IState) => {
  const products: IProductData[] = Selectors.getProducts(state);
  const language = LanguageSelectors.getLanguage(state);
  const isDiscounted = Selectors.isDiscounted(state);
  const session = AuthSelectors.getToken(state);
  const isPremium = AuthSelectors.isPremium(state);
  const subscriptions = ManageSelectors.getSubscriptions(state);
  const isLoggedIn = AuthSelectors.isLoggedIn(state);

  return {
    products,
    language,
    isDiscounted,
    session,
    isPremium,
    subscriptions,
    isLoggedIn,
  };
};

const mapDispatchToProps = {
  onProductSelect: SubscribeActions.selectProduct,
  onStartManageApp: ManageActions.startManageApp,
};

const SubscribeSection = connect(mapStateToProps, mapDispatchToProps)(SubscribeApp);
SubscribeSection.displayName = 'SubscribeSection';
export default SubscribeSection;
